import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiUsers, FiCopy, FiUpload, FiDownload, FiFilter } from 'react-icons/fi';
import api from '../api';

export default function Tests() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pointBalance, setPointBalance] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [results, setResults] = useState([]);
  const [testStats, setTestStats] = useState(null);
  const [relatedTests, setRelatedTests] = useState([]);
  const [templateStats, setTemplateStats] = useState(null);
  // 필터 상태
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    classGroupId: '',
    testDate: format(new Date(), 'yyyy-MM-dd'),
    totalScore: 100,
    description: '',
    isTemplate: false
  });
  // 파일 업로드 상태
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [testsRes, classesRes, pointsRes] = await Promise.all([
        api.get('/api/tests'),
        api.get('/api/class-groups', { params: { active: true } }),
        api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }))
      ]);
      setTests(testsRes.data.tests || []);
      setClasses(classesRes.data.classGroups || []);
      setPointBalance(pointsRes.data.balance || 0);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 과목 목록 추출 (중복 제거)
  const subjects = useMemo(() => {
    const subjectSet = new Set();
    tests.forEach(t => {
      if (t.subject) subjectSet.add(t.subject);
    });
    return Array.from(subjectSet).sort();
  }, [tests]);

  // 필터링된 시험 목록
  const filteredTests = useMemo(() => {
    if (subjectFilter === 'all') return tests;
    return tests.filter(t => t.subject === subjectFilter);
  }, [tests, subjectFilter]);

  const loadStudents = async (classGroupId) => {
    try {
      if (classGroupId) {
        // 반이 지정된 경우: 해당 반의 학생만 로드
        const { data } = await api.get(`/api/class-groups/${classGroupId}`);
        setStudents(data.classGroup?.students || []);
      } else {
        // 반이 없는 경우: 전체 학생 로드
        const { data } = await api.get('/api/users', { params: { role: 'student' } });
        setStudents(data.users || []);
      }
    } catch (error) {
      console.error('Failed to load students:', error);
      setStudents([]);
    }
  };

  const openModal = (test = null, duplicate = false) => {
    if (test) {
      setEditingTest(duplicate ? null : test);
      setFormData({
        name: duplicate ? `${test.name} (복사본)` : test.name,
        subject: test.subject || '',
        classGroupId: test.classGroupId?._id || test.classGroupId || '',
        testDate: duplicate ? format(new Date(), 'yyyy-MM-dd') : format(new Date(test.testDate), 'yyyy-MM-dd'),
        totalScore: test.totalScore,
        description: test.description || '',
        isTemplate: duplicate ? false : (test.isTemplate || false),
        templateId: duplicate ? test._id : (test.templateId || null)
      });
    } else {
      setEditingTest(null);
      setFormData({
        name: '',
        subject: '',
        classGroupId: '',
        testDate: format(new Date(), 'yyyy-MM-dd'),
        totalScore: 100,
        description: '',
        isTemplate: false,
        templateId: null
      });
    }
    setSelectedFile(null);
    setShowModal(true);
  };

  // 시험지 복제 (템플릿 → 새 시험)
  const handleDuplicate = (test) => {
    openModal(test, true);
  };

  // 파일 다운로드 핸들러 (서버 프록시 사용)
  const handleDownload = (testId) => {
    // 토큰을 포함한 다운로드 URL로 직접 이동 (서버 헤더 그대로 사용)
    const token = localStorage.getItem('token');
    const downloadUrl = `${process.env.REACT_APP_API_URL || ''}/api/tests/${testId}/download?token=${token}`;
    window.open(downloadUrl, '_blank');
  };

  // 파일 선택 핸들러
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 10MB 제한 체크
    if (file.size > 10 * 1024 * 1024) {
      alert('파일 크기는 10MB를 초과할 수 없습니다.');
      e.target.value = '';
      return;
    }

    // 허용 형식 체크 (mimetype + 확장자)
    const allowedTypes = [
      'application/pdf',
      'image/jpeg', 'image/png', 'image/gif',
      'application/x-hwp', 'application/haansofthwp', 'application/vnd.hancom.hwp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const ext = file.name.toLowerCase().split('.').pop();
    const allowedExts = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'hwp', 'doc', 'docx'];

    if (!allowedTypes.includes(file.type) && !allowedExts.includes(ext)) {
      alert('PDF, 이미지, HWP, Word 파일만 업로드 가능합니다.');
      e.target.value = '';
      return;
    }

    setSelectedFile(file);
  };

  // 포인트 체크 (파일 업로드용)
  const checkPointsForUpload = () => {
    const requiredPoints = 50;
    if (pointBalance < requiredPoints) {
      const result = window.confirm(
        `포인트가 부족합니다.\n\n` +
        `현재 포인트: ${pointBalance.toLocaleString()}P\n` +
        `필요 포인트: ${requiredPoints}P (시험지 업로드)\n\n` +
        `충전하시겠습니까?`
      );
      if (result) {
        navigate('/points');
      }
      return false;
    }
    return true;
  };

  const openResultModal = async (test) => {
    setSelectedTest(test);
    await loadStudents(test.classGroupId?._id || test.classGroupId);

    try {
      // GET /api/tests/:id returns { test, results, stats, relatedTests, templateStats }
      const { data } = await api.get(`/api/tests/${test._id}`);
      const existingResults = data.results || [];

      // Merge with students
      const resultMap = {};
      existingResults.forEach(r => {
        resultMap[r.studentId?._id || r.studentId] = r;
      });

      setResults(resultMap);
      setTestStats(data.stats || null);
      setRelatedTests(data.relatedTests || []);
      setTemplateStats(data.templateStats || null);
    } catch (error) {
      console.error('Failed to load results:', error);
      setResults({});
      setTestStats(null);
      setRelatedTests([]);
      setTemplateStats(null);
    }

    setShowResultModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 파일 업로드가 있으면 포인트 체크 (신규/수정 모두)
    if (selectedFile) {
      if (!checkPointsForUpload()) return;
    }

    try {
      setUploadingFile(true);
      let testId;

      if (editingTest) {
        await api.put(`/api/tests/${editingTest._id}`, formData);
        testId = editingTest._id;
      } else {
        const res = await api.post('/api/tests', formData);
        testId = res.data.test._id;
      }

      // 파일 업로드 (새 파일이 있는 경우)
      if (selectedFile) {
        const fileFormData = new FormData();
        fileFormData.append('file', selectedFile);

        await api.post(`/api/tests/${testId}/upload`, fileFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // 포인트 잔액 새로고침
        const pointsRes = await api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }));
        setPointBalance(pointsRes.data.balance || 0);
      }

      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '저장에 실패했습니다');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/api/tests/${id}`);
      loadData();
    } catch (error) {
      alert('삭제에 실패했습니다');
    }
  };

  const handleResultChange = (studentId, field, value) => {
    setResults(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  const saveResults = async () => {
    try {
      const resultData = Object.entries(results)
        .filter(([_, r]) => r.score !== undefined && r.score !== '')
        .map(([studentId, r]) => ({
          studentId,
          score: Number(r.score),
          feedback: r.feedback || '',
          showInReport: r.showInReport !== false  // 기본값 true
        }));

      await api.post(`/api/tests/${selectedTest._id}/results/bulk`, { results: resultData });
      alert('성적이 저장되었습니다');
      setShowResultModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '저장에 실패했습니다');
    }
  };

  const getAvgScore = (test) => {
    if (!test.resultCount || test.resultCount === 0) return '-';
    return test.avgScore?.toFixed(1) || '-';
  };

  return (
    <div className="tests-page">
      <div className="page-header">
        <h2>시험 / 성적</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* 과목 필터 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiFilter size={16} color="#6B7280" />
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="all">전체 과목</option>
              {subjects.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <FiPlus /> 시험 등록
          </button>
        </div>
      </div>

      {/* Tests List */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>시험일</th>
              <th>시험명</th>
              <th>과목</th>
              <th>반</th>
              <th>만점</th>
              <th>응시</th>
              <th>평균</th>
              <th>시험지</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="loading">로딩 중...</td></tr>
            ) : filteredTests.length === 0 ? (
              <tr><td colSpan="9" className="empty">등록된 시험이 없습니다</td></tr>
            ) : (
              filteredTests.map((test) => (
                <tr key={test._id} style={test.isTemplate ? { backgroundColor: '#FEF3C7' } : {}}>
                  <td>
                    {test.isTemplate ? (
                      <span style={{ color: '#D97706', fontWeight: 'bold' }}>템플릿</span>
                    ) : (
                      format(new Date(test.testDate), 'yyyy-MM-dd')
                    )}
                  </td>
                  <td>
                    {test.name}
                    {test.templateId && <span style={{ marginLeft: '6px', fontSize: '0.75rem', color: '#6B7280' }}>(복제됨)</span>}
                  </td>
                  <td>{test.subject || '-'}</td>
                  <td>{test.classGroupId?.name || '-'}</td>
                  <td>{test.totalScore}점</td>
                  <td>{test.resultCount || 0}명</td>
                  <td>{getAvgScore(test)}</td>
                  <td>
                    {test.attachmentUrl ? (
                      <button
                        className="icon-btn"
                        title={`다운로드: ${test.attachmentName || '시험지'}`}
                        style={{ color: '#059669' }}
                        onClick={() => handleDownload(test._id, test.attachmentName)}
                      >
                        <FiDownload />
                      </button>
                    ) : '-'}
                  </td>
                  <td>
                    <button className="icon-btn" onClick={() => openResultModal(test)} title="성적 입력">
                      <FiUsers />
                    </button>
                    <button className="icon-btn" onClick={() => handleDuplicate(test)} title="복제하여 새 시험">
                      <FiCopy />
                    </button>
                    <button className="icon-btn" onClick={() => openModal(test)} title="수정">
                      <FiEdit2 />
                    </button>
                    <button className="icon-btn danger" onClick={() => handleDelete(test._id)} title="삭제">
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Test Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTest ? '시험 수정' : '시험 등록'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>시험명 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="예: 1학기 중간고사"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>과목 *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="예: 수학, 영어, 국어"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>반 (선택)</label>
                  <select
                    value={formData.classGroupId}
                    onChange={(e) => setFormData({ ...formData, classGroupId: e.target.value })}
                  >
                    <option value="">전체 (반 지정 안함)</option>
                    {classes.map((c) => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>시험일 *</label>
                  <input
                    type="date"
                    value={formData.testDate}
                    onChange={(e) => setFormData({ ...formData, testDate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>만점 *</label>
                  <input
                    type="number"
                    value={formData.totalScore}
                    onChange={(e) => setFormData({ ...formData, totalScore: e.target.value })}
                    min="1"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>설명</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="시험 관련 메모"
                  rows={2}
                />
              </div>

              {/* 템플릿 체크박스 */}
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="isTemplate"
                  checked={formData.isTemplate}
                  onChange={(e) => setFormData({ ...formData, isTemplate: e.target.checked })}
                  style={{ width: '18px', height: '18px' }}
                />
                <label htmlFor="isTemplate" style={{ marginBottom: 0, cursor: 'pointer' }}>
                  템플릿으로 저장 (매년 재사용 가능)
                </label>
              </div>

              {/* 시험지 파일 업로드 */}
              <div className="form-group">
                <label>
                  <FiUpload style={{ marginRight: '6px' }} />
                  시험지 첨부 (선택)
                  <span style={{ fontSize: '0.8rem', color: '#6B7280', marginLeft: '8px' }}>
                    PDF/이미지/HWP/Word, 최대 10MB, 50P 차감
                  </span>
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.hwp,.doc,.docx,image/*"
                  onChange={handleFileSelect}
                  style={{ padding: '8px' }}
                />
                {selectedFile && (
                  <p style={{ fontSize: '0.85rem', color: '#059669', marginTop: '4px' }}>
                    선택됨: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)}MB)
                  </p>
                )}
                {editingTest?.attachmentUrl && !selectedFile && (
                  <p style={{ fontSize: '0.85rem', color: '#6B7280', marginTop: '4px' }}>
                    기존 파일 있음 (새 파일 선택 시 교체됨)
                  </p>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploadingFile}>
                  {uploadingFile ? '저장 중...' : (editingTest ? '수정' : '등록')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {showResultModal && selectedTest && (
        <div className="modal-overlay" onClick={() => setShowResultModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>성적 입력 - {selectedTest.name}</h3>
              <button className="close-btn" onClick={() => setShowResultModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <p className="text-muted">만점: {selectedTest.totalScore}점</p>

                {/* 이번 시험 통계 */}
                {testStats && testStats.count > 0 && (
                  <div style={{ display: 'flex', gap: '15px', marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    <span><strong>이번 시험 평균:</strong> {testStats.average}점</span>
                    <span><strong>최고:</strong> {testStats.highest}점</span>
                    <span><strong>최저:</strong> {testStats.lowest}점</span>
                    <span><strong>응시:</strong> {testStats.count}명</span>
                  </div>
                )}

                {/* 템플릿 평균 (관련 시험들의 평균) */}
                {templateStats && (
                  <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '6px', border: '1px solid #2196f3' }}>
                    <div style={{ marginBottom: '5px' }}>
                      <strong style={{ color: '#1976d2' }}>📊 템플릿 그룹 평균</strong>
                      <span style={{ marginLeft: '8px', fontSize: '0.85rem', color: '#666' }}>
                        (이 시험 포함 총 {templateStats.testCount}개 시험)
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '15px' }}>
                      <span><strong>평균:</strong> {templateStats.average}점</span>
                      <span><strong>최고:</strong> {templateStats.highest}점</span>
                      <span><strong>최저:</strong> {templateStats.lowest}점</span>
                      <span><strong>총 응시:</strong> {templateStats.resultCount}명</span>
                    </div>
                  </div>
                )}

                {/* 관련 시험 목록 */}
                {relatedTests && relatedTests.length > 0 && (
                  <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3e0', borderRadius: '6px', border: '1px solid #ff9800' }}>
                    <div style={{ marginBottom: '5px' }}>
                      <strong style={{ color: '#f57c00' }}>🔗 같은 템플릿에서 생성된 시험</strong>
                    </div>
                    <div style={{ fontSize: '0.9rem' }}>
                      {relatedTests.map(rt => (
                        <div key={rt._id} style={{ padding: '4px 0', color: '#666' }}>
                          • {rt.name} {rt.testDate && `(${format(new Date(rt.testDate), 'yyyy-MM-dd')})`}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {students.length === 0 ? (
                <p className="empty">해당 반에 등록된 학생이 없습니다</p>
              ) : (
                <table className="results-table">
                  <thead>
                    <tr>
                      <th>학생명</th>
                      <th>점수</th>
                      <th>피드백</th>
                      <th style={{ width: '120px' }}>수업일지 포함</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr key={student._id}>
                        <td>{student.name}</td>
                        <td>
                          <input
                            type="number"
                            value={results[student._id]?.score ?? ''}
                            onChange={(e) => handleResultChange(student._id, 'score', e.target.value)}
                            min="0"
                            max={selectedTest.totalScore}
                            placeholder="점수"
                            style={{ width: '80px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={results[student._id]?.feedback ?? ''}
                            onChange={(e) => handleResultChange(student._id, 'feedback', e.target.value)}
                            placeholder="피드백 (선택)"
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={results[student._id]?.showInReport !== false}
                            onChange={(e) => handleResultChange(student._id, 'showInReport', e.target.checked)}
                            title="체크 해제 시 이 학생의 성적이 수업일지/리포트에 표시되지 않습니다"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowResultModal(false)}>
                취소
              </button>
              <button type="button" className="btn btn-primary" onClick={saveResults}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
